<template>
  <v-card class="detailed-select-root elevation-0">
    <v-card-text>
      <v-simple-table class="detailed-select-table">
        <template #default>
          <thead>
            <tr>
              <th 
                v-for="header in columns.map(c => c.header)" 
                :key="header.text"
                :class="alignmentClass(header)">
                {{ header.text }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in items" :key="item.text">
              <td 
                v-for="column in columns"
                :key="column.header.text"
                :class="[{ 'selected-row': isSelected(item) }, ...alignmentClass(column.header)]"
                @click="selectItem(item)"
              >
                <slot 
                  :name="'cell-' + column.cell.key" 
                  :item="item"
                  :column="column"
                >
                  <template v-if="item[column.cell.key]">
                    {{item[column.cell.key]}}
                  </template>
                </slot>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'DetailedSelect',
  props: {
    value: {
      type: String,
      default: null
    },
    columns: {
      type: Array,
      required: true
    },
    items: {
      type: Array,
      default: () => []
    }
  },
  methods: {
    selectItem(item) {
      if (this.value !== item._id) {
        this.$emit('input', item._id);
        this.$emit('change', item);
      }
    },
    isSelected(item) {
      return this.value === item._id;
    },
    alignmentClass(header) {
      return [
        header.align === 'end' ? 'text-end' : 'text-start',
      ];
    },
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/material-dashboard/variables.scss';

.detailed-select-root {
  min-width: 290px;
  border-radius: 1rem;
  overflow: auto;
  background-color: $background-paper;

  .v-card__text {
    padding: 0;
  }
}

::v-deep(.v-data-table.detailed-select-table) {
  & > .v-data-table__wrapper > table {
    padding: 8px 0;

    // Header
    th {
      padding: 12px 16px;
      font-weight: 700;
      font-family: $body-font-family;
      color: $text-secondary;
    }

    // Body
    > tbody > tr {
      cursor: pointer;

      td {
        line-height: 1em;
        padding: 4px 16px;
        & + td {
          padding-left: 0;
        }
        
        &.selected-row {
          cursor: default;
          background-color: $primary-stripped-active;
          color: $primary-stripped-contrast;

          .color-text-secondary {
            color: $primary-stripped-contrast;
            opacity: 0.8;
          }
        }
      }

      &:hover {
        &:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
          background-color: $background-paper;
        }
        td:not(.selected-row) {
          background-color: $background-paper-hover;
        }
      }
      &:active {
        &:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
          background-color: $background-paper;
        }
        td:not(.selected-row) {
          background-color: $background-paper-active;
        }
      }
    }
  }
}
</style>


<!-- DOCUMENTATION --

DetailedSelect.vue

  Description:
  This component renders a selectable table with detailed cell customization options and row selection capabilities.

  Props:
  - value: String             (Optional)              The selected item's ID
  - columns: Array            (Required)              Array of column definitions, format:
                                                      [{
                                                        header: {
                                                          text: String,        // Column header text
                                                          align?: String      // Text alignment (start/end)
                                                        },
                                                        cell: {
                                                          key: String         // Key to access data in items
                                                        }
                                                      }]
  - items: Array              (Optional)              Array of data objects to display in the table


  Example:
  1. Basic Usage with Two Columns:
  <detailed-select
    :value="selectedId"
    :columns="getColumnsSettings"
    :items="items"
    @input="value => selectItem(value)"
  >
    <template #cell-identifier="{ item }">            // Custom cell rendering using named slots
      <div class="d-flex flex-column">                    Other than "key=identifier" will be a simple text
        <strong>{{ item.identifier }}</strong>
        <small>{{ item.mixDesignCode }}</small>
      </div>
    </template>
  </detailed-select>

  getColumnsSettings() {
    return [
      {
        header: { text: 'Mix' },
        cell: { key: 'identifier' }                   // Custom cell rendering using named slots "key=identifier"
      },
      {
        header: { text: 'Code', align: 'end' },
        cell: { key: 'mixDesignCode' }
      }
    ];
  }

-->
